import { createApi } from '@reduxjs/toolkit/query/react';
import { z } from 'zod';

import { config } from '../config';
import { Payment, PolicyTransactionInformation } from '../types/policy';
import { defaultHeadersCommercialInsurance } from './defaultHeaders';
import { createCustomBaseQuery } from './utils/customBaseQuery';

export interface RenewPolicyRequest {
  policyNumber: string;
  policy: {
    policyHeader: {
      policyTransactionInformation: PolicyTransactionInformation;
      issueDocument: boolean;
    };
    payment: Payment;
  };
}

export interface RenewPolicyResponse {
  messageMetadata: {
    messageHeader: {
      status: string;
    };
    transactionInformation: {
      identifier: string;
    };
  };
  policy: {
    policyHeader: {
      policyTransactionInformation: {
        policyNumber: string;
      };
    };
  };
}

export type PolicyError = {
  error: {
    type: 'NOT_FOUND' | 'UNKNOWN';
  };
};

export type GetPolicyResponse = {
  policyNumber: string;
  /**
   *   '01': 'Currently in Force',
   *   '02': 'Manual rerate required',
   *   '03': 'Rerate Done',
   *   '04': 'Renewal produced',
   *   '08': 'Lapsed',
   *   '09': 'Cancelled',
   */
  policyStatus: string;
  policyType: string;
  dueAmount: number;
  dueDate: string;
};

export const errorSchema = z.object({
  errors: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
      extendedMessages: z.optional(z.array(z.object({ message: z.string() }))),
    })
  ),
});

const customBaseQuery = createCustomBaseQuery({
  baseUrl: config.msCommercialInsApi,
  defaultHeaders: defaultHeadersCommercialInsurance,
  reducerPath: 'policyApi',
});

export const policyApi = createApi({
  reducerPath: 'policyApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getPolicy: builder.query<
      GetPolicyResponse | PolicyError,
      {
        policyNumber: string;
      }
    >({
      query: ({ policyNumber }) => ({
        url: `/policies/${policyNumber}`,
        validateStatus: ({ status }, result) =>
          status === 200 || (status === 400 && result.error?.type === 'NOT_FOUND'),
        responseHandler: async (response) => {
          if (response.status === 400) {
            const error = errorSchema.parse(await response.json());
            if (
              error.errors[0]?.code === 'CCHANGE_BUSINESS_EXCEPTION' &&
              error.errors[0]?.extendedMessages?.[0]?.message?.startsWith('POLICY NOT ON FILE')
            ) {
              return {
                error: {
                  type: 'NOT_FOUND',
                },
              };
            }
            return {
              error: {
                type: 'UNKNOWN',
              },
            };
          }
          return response.json();
        },
      }),
    }),
    renew: builder.mutation<RenewPolicyResponse, RenewPolicyRequest>({
      query: ({ policyNumber, ...body }) => ({
        url: `/policies/${policyNumber}/renew`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const { useLazyGetPolicyQuery } = policyApi;
